import { Container, Flex } from '@chakra-ui/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useResource, useResourceRelationship } from 'datx-jsonapi-react';
import { FC, useCallback, useMemo } from 'react';

import { SpeakersGrid } from '@/components/ui/features/hubs-speakers/SpeakersGrid/SpeakersGrid';
import { HubsPageActions } from '@/components/ui/shared/hubs/HubsPageActions/HubsPageActions';
import { ThinProfileSort } from '@/enums/ThinProfileSort';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { getHubBySlug, getHubsSubtree } from '@/queries/hubs';
import { thinProfileInfiniteQueryOptions } from '@/queries/upgrade/thinProfiles';

interface ILibraryFiltersPageContentParams {
  slug: string;
  query?: string;
  event_id: string;
  sort: ThinProfileSort;
}

const sortingOptions = [
  {
    label: 'lastNameAscending',
    value: ThinProfileSort.LastName,
  },
  {
    label: 'lastNameDescending',
    value: `-${ThinProfileSort.LastName}`,
  },
];

export const SpeakersContent: FC = () => {
  const { slug, query, event_id, sort } = useQueryParams<ILibraryFiltersPageContentParams>({
    sort: StringParam,
    slug: StringParam,
    query: StringParam,
    string: StringParam,
  });

  const { data } = useResource(getHubBySlug(slug));
  const {
    data: { subtreeIds },
  } = useResourceRelationship(getHubsSubtree(data.id));

  const { data: infiniteData, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    thinProfileInfiniteQueryOptions(
      { subject_id: subtreeIds, query, event_id, scope: 'with_library_lectures' },
      30,
      sort
    )
  );

  const items = useMemo(() => infiniteData?.pages?.flatMap((page) => page.data) ?? [], [infiniteData]);
  const meta = infiniteData?.pages?.[0]?.meta;

  const isReachingEnd = !hasNextPage;
  const isLoadingMore = isFetchingNextPage;

  const onLoadMore = useCallback(() => fetchNextPage(), [fetchNextPage]);

  return (
    <Container>
      <Flex flexDir="column" gap={4}>
        <HubsPageActions
          sortingOptions={sortingOptions}
          defaultOption={ThinProfileSort.LastName}
          totalCount={meta?.total_count}
        />
        <SpeakersGrid
          items={items}
          isLoadingMore={isLoadingMore}
          isEmpty={items.length === 0 && !isLoadingMore}
          isReachingEnd={isReachingEnd}
          onLoadMore={onLoadMore}
          error={(error as unknown) as Error}
          pageSize={12}
        />
      </Flex>
    </Container>
  );
};
